import { useDirectOAuth2Authorize } from "@queries/auth/useDirectOAuth2Authorize";
import { useGetOAuth2ClientInfo } from "@queries/auth/useGetOAuth2ClientInfo";
import { toast } from "@shared/services/toast";
import { getSafeRedirectTarget } from "@shared/utils/getSafeRedirectTarget";
import { consumeSsoLoginRedirect } from "@shared/utils/sso-login-redirect";
import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  LOGIN_SCHEMA_DEFAULT_VALUES,
  loginFormSchema,
  LoginFormSchema,
} from "../auth-form-helpers";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { appendQueryParam } from "@shared/utils/appendQueryParam";
import { useLogin } from "@queries/auth/useLogin";

type UseLoginProps = {
  oauthData: {
    clientId: string;
    scope: string;
    redirectUri: string;
    state: string | null;
    responseType: string;
  } | null;
  redirect: string | null;
  welcome: string | null;
};

export const useLoginLogic = (props: UseLoginProps) => {
  const { oauthData, redirect, welcome } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const defaultEmail = location.state?.email;
  const loginFormMethods = useForm<LoginFormSchema>({
    defaultValues: {
      ...LOGIN_SCHEMA_DEFAULT_VALUES,
      email: defaultEmail ?? LOGIN_SCHEMA_DEFAULT_VALUES.email,
    },
    resolver: yupResolver(loginFormSchema),
    reValidateMode: "onChange",
  });

  useEffect(() => {
    if (!oauthData) return;

    const isOAuthDataValid =
      !!oauthData.clientId &&
      !!oauthData.redirectUri &&
      !!oauthData.responseType &&
      !!oauthData.scope;

    if (!isOAuthDataValid) {
      toast.error("Missing parameters.", { toastId: "missing-params" });
      navigate("/", { replace: true });
    }
  }, [oauthData, navigate]);

  const continueAsAuthenticatedUser = useCallback(() => {
    const safeRedirect = getSafeRedirectTarget(redirect);
    if (safeRedirect) {
      window.location.replace(safeRedirect);
      return;
    }
    navigate("/");
  }, [redirect, navigate]);

  const directOAuth2AuthorizeMutation = useDirectOAuth2Authorize({
    onSuccess: (data) => {
      window.location.replace(data.redirectUrl);
    },
  });

  const oauthInfoQuery = useGetOAuth2ClientInfo({
    id: oauthData?.clientId || null,
    onError: () => {
      navigate("/login");
    },
  });

  const loginMutation = useLogin({
    onSuccess: (data) => {
      if (data.authFlow === "CHALLENGE") {
        navigate(authChallengeNewPasswordRequiredPath, {
          state: {
            email: loginFormMethods.getValues("email").trim(),
            challengeName: data.challengeName,
            session: data.session,
            redirect: redirect
              ? appendQueryParam(redirect, "welcome", welcome)
              : redirect,
          },
        });
        return;
      }

      if (data.authFlow === "LOGIN") {
        consumeSsoLoginRedirect();
        const safeRedirect = getSafeRedirectTarget(redirect);
        window.location.replace(safeRedirect || "/");
        return;
      }

      toast.error(
        "Login failed. Please try again later and if the error persists, please contact the support.",
      );
    },
    onError: () => {
      loginFormMethods.setValue("password", "");
    },
  });

  const authChallengeNewPasswordRequiredPath = location.search
    ? `/auth-challenge/new-password-required${location.search}`
    : "/auth-challenge/new-password-required";

  const loginOAuth2AuthorizeMutation = useLogin({
    onSuccess: (data) => {
      if (data.authFlow === "CHALLENGE") {
        navigate(authChallengeNewPasswordRequiredPath, {
          state: {
            email: loginFormMethods.getValues("email").trim(),
            challengeName: data.challengeName,
            session: data.session,
            redirect: redirect
              ? appendQueryParam(redirect, "welcome", welcome)
              : redirect,
          },
        });
        return;
      }

      if (!oauthData) {
        window.location.replace("/");
        return;
      }

      if (
        !oauthData.clientId ||
        !oauthData.redirectUri ||
        !oauthData.responseType ||
        !oauthData.scope
      ) {
        toast.error("Invalid params.");
        return;
      }

      directOAuth2AuthorizeMutation.mutate({
        ...oauthData,
      });
    },
  });

  return {
    oauthInfoQuery,
    directOAuth2AuthorizeMutation,
    loginFormMethods,
    loginMutation,
    loginOAuth2AuthorizeMutation,
    continueAsAuthenticatedUser,
  };
};
